/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { NO_SPRINT_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/filters/sprint-filter';
import {
	getSprintStateLozengeContent,
	getLozengeAppearance,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/sprints/utils';
import { SPRINT_FILTER_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/view-settings/filters/types.tsx';
import filterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/messages';
import sprintFilterMessages from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/top/filter-bar/sprint-filter/messages';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from '../styles.module.css';
import messages from './messages';
import type { SprintFilterGroupProps } from './types';

export const SPRINT_COUNTS_LIMIT = 2;

const SprintLozenge = ({ sprintState }: { sprintState?: string }) => {
	const intl = useIntl();
	if (sprintState === undefined) {
		return null;
	}
	const lozengeContent = getSprintStateLozengeContent(intl);

	if (fg('replace_typography_css_with_tokens_arj')) {
		return (
			<div css={exportPNGLozengeStyle}>
				<Lozenge appearance={getLozengeAppearance(sprintState)}>
					{lozengeContent(sprintState)}
				</Lozenge>
			</div>
		);
	}
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className={styles.exportPNGLozenge}>
			<Lozenge appearance={getLozengeAppearance(sprintState)}>
				{lozengeContent(sprintState)}
			</Lozenge>
		</div>
	);
};

export default function SprintFilterGroup({
	sprintCategories,
	additionalSprints,
}: SprintFilterGroupProps) {
	const intl = useIntl();

	// only the additional selected sprints are truncated, the sprint categories will always be displayed
	let remainderItemCounts =
		additionalSprints.length > 0
			? // the remainder count is the additional sprints minus the limit subtracted by the number of categories,
				// and should not exceed the total number of additional sprints
				additionalSprints.length - Math.max(SPRINT_COUNTS_LIMIT - sprintCategories.length, 0)
			: 0;

	// if the remainder item count is 1, might as well display the last item
	remainderItemCounts = remainderItemCounts === 1 ? 0 : remainderItemCounts;
	// slice the additional sprints based on the remainder item counts
	const truncatedSprints =
		remainderItemCounts > 0
			? additionalSprints.slice(0, additionalSprints.length - remainderItemCounts)
			: additionalSprints;
	return (
		<>
			{fg('replace_typography_css_with_tokens_arj') ? (
				<Text as="strong">{intl.formatMessage(filterMessages[SPRINT_FILTER_ID])}</Text>
			) : (
				// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
				<strong>{intl.formatMessage(filterMessages[SPRINT_FILTER_ID])} </strong>
			)}
			<span>
				(
				{sprintCategories.map((item, i) => (
					<span key={item.id === NO_SPRINT_ID ? 'NO_SPRINT_ID' : item.id}>
						{item.id === NO_SPRINT_ID ? (
							intl.formatMessage(commonMessages.noSprintLabel)
						) : (
							<FormattedMessage
								{...sprintFilterMessages.categoriesLabel}
								values={{ state: <SprintLozenge sprintState={item.sprintState} /> }}
							/>
						)}
						{i < sprintCategories.length - 1 ||
						truncatedSprints.length > 0 ||
						remainderItemCounts > 0
							? ',  '
							: ''}
					</span>
				))}
				{truncatedSprints.map((item, i) => (
					<span key={item.id}>
						{item.sprintName} <SprintLozenge sprintState={item.sprintState} />
						{i < truncatedSprints.length - 1 || remainderItemCounts > 0 ? ',  ' : ''}
					</span>
				))}
				{remainderItemCounts > 0
					? `${intl.formatMessage(messages.remainingSprints, {
							count: remainderItemCounts,
						})}`
					: ''}
				)
			</span>
		</>
	);
}

const exportPNGLozengeStyle = css({
	display: 'inline-flex',
	alignItems: 'center',
});
